/**
 * Strict runtime parser for draft PR23 saved-group additional analysis.
 *
 * TypeScript disappears at runtime. A stale/tampered proxy response, a foreign
 * project/group, an unknown key or a result that claims semantic completion without
 * an approved model must never be rendered as trusted findings. One invalid field
 * fails the whole value closed.
 */

import type {
  GroupAnalysisCapabilities,
  JobDetail,
  GroupAnalysisLimits,
  GroupAnalysisPreflight,
  GroupAnalysisResult,
  GroupAnalysisRun,
  GroupAnalysisRunPage,
  OpportunityGroupSource,
} from "../api/client";

export interface GroupAnalysisPreflightExpectation {
  groupVersion: number;
  source: OpportunityGroupSource;
  organizationIds: string[];
}

export interface GroupAnalysisResultExpectation {
  groupVersion: number;
  source: OpportunityGroupSource;
  snapshotHash?: string;
}

export interface GroupAnalysisRunExpectation {
  groupVersion?: number;
  source?: OpportunityGroupSource;
  snapshotHash?: string;
}

export interface GroupAnalysisLoader {
  getJob(projectId: string, jobId: string): Promise<JobDetail>;
  getGroupAnalysisPreflight(projectId: string, groupId: string): Promise<unknown>;
  startGroupAnalysis(
    projectId: string,
    groupId: string,
    input: { group_version: number; question: string; idempotency_key: string },
  ): Promise<unknown>;
  listGroupAnalysisRuns(
    projectId: string,
    groupId: string,
    options?: { cursor?: string | null; limit?: number },
  ): Promise<unknown>;
}

const GROUP_ID = /^ogp_[0-9a-f]{32}$/;
const JOB_ID = /^job_[0-9a-z_]+$/;
const ORG_ID = /^org_[0-9a-z_]+$/;
const DIGEST = /^sha256:[0-9a-f]{64}$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
const HOST = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;
const MAX_TEXT = 1000;
const MAX_URL = 2048;
const MAX_ORGANIZATIONS = 20;

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: UnknownRecord, keys: readonly string[]): boolean {
  return Object.keys(value).length === keys.length && keys.every((key) =>
    Object.prototype.hasOwnProperty.call(value, key),
  );
}

function isText(value: unknown, maximum = MAX_TEXT, minimum = 1): value is string {
  return typeof value === "string" && value.length >= minimum && value.length <= maximum;
}

function isDigest(value: unknown): value is string {
  return typeof value === "string" && DIGEST.test(value);
}

function isInt(value: unknown, minimum: number, maximum: number): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= minimum && value <= maximum;
}

function isDate(value: unknown): value is string {
  return typeof value === "string" && DATE.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));
}

function isDateTime(value: unknown): value is string {
  return typeof value === "string" && DATE_TIME.test(value) && !Number.isNaN(Date.parse(value));
}

function isUrl(value: unknown, maximum = 2048): value is string {
  if (!isText(value, maximum)) return false;
  try {
    const parsed = new URL(value);
    return (parsed.protocol === "http:" || parsed.protocol === "https:")
      && Boolean(parsed.hostname) && !parsed.username && !parsed.password
      && !parsed.hash;
  } catch {
    return false;
  }
}

function urlHost(value: string): string | null {
  try {
    const host = new URL(value).hostname.toLowerCase();
    return host && HOST.test(host) ? host : null;
  } catch {
    return null;
  }
}

function siteRoot(host: string): string {
  return host.startsWith("www.") ? host.slice(4) : host;
}

function hostInSite(value: string, root: string): boolean {
  const host = urlHost(value);
  return host !== null && (host === root || host.endsWith(`.${root}`));
}

function isStringArray(value: unknown, maximum: number, itemMaximum = 128): value is string[] {
  return Array.isArray(value) && value.length <= maximum
    && value.every((item) => isText(item, itemMaximum));
}

export function sameGroupAnalysisSource(
  left: OpportunityGroupSource | null | undefined,
  right: OpportunityGroupSource | null | undefined,
): boolean {
  if (!left || !right) return left === right;
  const keys = [
    "job_id", "decisions_hash", "analysis_output_hash", "review_output_hash",
    "snapshot_hash", "evaluated_on",
  ] as const;
  if (!keys.every((key) => left[key] === right[key])) return false;
  return left.workflow.key === right.workflow.key
    && left.workflow.version === right.workflow.version
    && left.workflow.content_hash === right.workflow.content_hash;
}

function sameStringArray(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((item, index) => item === right[index]);
}

function isSource(value: unknown): boolean {
  if (!isRecord(value)) return false;
  const keys = [
    "job_id", "decisions_hash", "analysis_output_hash", "review_output_hash",
    "snapshot_hash", "workflow", "evaluated_on",
  ];
  if (!hasExactKeys(value, keys)) return false;
  if (typeof value.job_id !== "string" || !JOB_ID.test(value.job_id)
    || !isDigest(value.decisions_hash) || !isDigest(value.analysis_output_hash)
    || !isDigest(value.review_output_hash) || !isDigest(value.snapshot_hash)
    || !isDate(value.evaluated_on)) return false;
  const workflow = value.workflow;
  return isRecord(workflow) && hasExactKeys(workflow, ["key", "version", "content_hash"])
    && workflow.key === "megabooker.opportunity_analysis"
    && workflow.version === 1 && isDigest(workflow.content_hash);
}

function parseCaps(value: unknown, stage: "available" | "completed"): GroupAnalysisCapabilities | null {
  if (!isRecord(value)) return null;
  const keys = ["existing_data_elaboration", "official_site_research", "semantic_analysis",
    "semantic_provider", "semantic_reason"];
  if (!hasExactKeys(value, keys)) return null;
  if (
    value.existing_data_elaboration !== stage
    || (stage === "available"
      && value.official_site_research !== "available"
      && value.official_site_research !== "unavailable")
    || (stage === "completed"
      && value.official_site_research !== "completed"
      && value.official_site_research !== "unavailable")
    || value.semantic_analysis !== "not_evaluated"
    || value.semantic_provider !== "not_configured"
    || value.semantic_reason !== "semantic_provider_not_activated_for_product"
  ) return null;
  return value as unknown as GroupAnalysisCapabilities;
}

export const GROUP_ANALYSIS_LIMITS: GroupAnalysisLimits = {
  version: "group_analysis.policy.v1",
  max_organizations: 20,
  max_pages_per_organization: 5,
  max_depth: 1,
  max_page_bytes: 1048576,
  request_timeout_seconds: 10,
  max_run_seconds: 300,
  concurrency: 2,
  max_redirects: 3,
  robots: "required",
};

function parseLimits(value: unknown): GroupAnalysisLimits | null {
  if (!isRecord(value)) return null;
  const expected = GROUP_ANALYSIS_LIMITS;
  const keys = Object.keys(expected) as (keyof GroupAnalysisLimits)[];
  if (!hasExactKeys(value, keys)) return null;
  return keys.every((key) => value[key] === expected[key]) ? expected : null;
}

function parseHost(value: unknown, organizationId: string): boolean {
  if (!isRecord(value) || value.organization_id !== organizationId) return false;
  if (value.status === "needs_verification") {
    return hasExactKeys(value, ["organization_id", "status", "reason"])
      && isText(value.reason, 64);
  }
  if (value.status !== "eligible") return false;
  if (!hasExactKeys(value, ["organization_id", "status", "host", "url", "evidence"])) return false;
  if (typeof value.host !== "string" || !HOST.test(value.host) || !isUrl(value.url)) return false;
  if (new URL(value.url as string).hostname.replace(/^www\./, "") !== value.host.replace(/^www\./, "")) {
    return false;
  }
  const evidence = value.evidence;
  if (!isRecord(evidence)) return false;
  const keys = ["source_id", "source_url", "observed_at", "website_value", "claim", "evidence_hash"];
  return hasExactKeys(evidence, keys)
    && isText(evidence.source_id, 64) && isUrl(evidence.source_url)
    && isDate(evidence.observed_at) && isUrl(evidence.website_value)
    && isText(evidence.claim, 1000) && isDigest(evidence.evidence_hash);
}

export function parseGroupAnalysisPreflight(
  value: unknown, projectId: string, groupId: string,
  expected?: GroupAnalysisPreflightExpectation,
): GroupAnalysisPreflight | null {
  if (!isRecord(value)) return null;
  const keys = ["schema_version", "state", "project_id", "reference_date", "group",
    "source", "question", "organizations", "official_hosts", "capabilities", "limits",
    "model_activation"];
  if (!hasExactKeys(value, keys)) return null;
  if (value.schema_version !== "group_analysis.preflight.v1" || value.state !== "ready"
    || value.project_id !== projectId || !isDate(value.reference_date)) return null;
  const group = value.group;
  if (!isRecord(group) || !hasExactKeys(group, [
    "id", "name", "note", "version", "organization_count", "organization_ids",
  ])) return null;
  if (group.id !== groupId || typeof group.id !== "string" || !GROUP_ID.test(group.id)) return null;
  if (!isText(group.name, 80) || !group.name.trim() || typeof group.note !== "string"
    || group.note.length > 2000 || !isInt(group.version, 1, 1_000_000)) return null;
  const ids = group.organization_ids;
  if (!Array.isArray(ids) || ids.length < 1 || ids.length > MAX_ORGANIZATIONS
    || new Set(ids).size !== ids.length
    || !ids.every((id) => typeof id === "string" && ORG_ID.test(id))) return null;
  if (group.organization_count !== ids.length || !isSource(value.source)) return null;
  if (expected) {
    if (group.version !== expected.groupVersion
      || !sameStringArray(ids as string[], expected.organizationIds)
      || !sameGroupAnalysisSource(value.source as OpportunityGroupSource, expected.source)) {
      return null;
    }
  }
  const question = value.question;
  if (!isRecord(question) || !hasExactKeys(question, ["max_length", "suggested"])
    || question.max_length !== 2000 || typeof question.suggested !== "string"
    || question.suggested.length > 2000) return null;
  const organizations = value.organizations;
  if (!Array.isArray(organizations) || organizations.length !== ids.length) return null;
  const seen = new Set<string>();
  for (let index = 0; index < organizations.length; index += 1) {
    const item = organizations[index];
    if (!isRecord(item) || !hasExactKeys(item, ["organization_id", "organization_name", "official_host"])) return null;
    if (item.organization_id !== ids[index] || !parseHost(item.official_host, ids[index] as string)) return null;
    if (item.organization_name !== null && !isText(item.organization_name, 200)) return null;
    if (seen.has(item.organization_id as string)) return null;
    seen.add(item.organization_id as string);
  }
  const hosts = value.official_hosts;
  if (!isRecord(hosts) || !hasExactKeys(hosts, ["eligible", "needs_verification", "max"])) return null;
  const eligible = organizations.filter((item) => isRecord(item) && isRecord(item.official_host)
    && item.official_host.status === "eligible").length;
  if (hosts.eligible !== eligible || hosts.needs_verification !== organizations.length - eligible
    || hosts.max !== 20) return null;
  const caps = parseCaps(value.capabilities, "available");
  if (!caps || !parseLimits(value.limits)) return null;
  if ((eligible === 0) !== (caps.official_site_research === "unavailable")) return null;
  const model = value.model_activation;
  if (!isRecord(model) || !hasExactKeys(model, ["status", "note"])
    || model.status !== "not_requested" || !isText(model.note, 500)) return null;
  return value as unknown as GroupAnalysisPreflight;
}

const JOB_STATUSES = new Set([
  "queued", "running", "awaiting_approval", "awaiting_budget_approval",
  "succeeded", "failed", "cancelled", "killed", "needs_review",
]);

export function parseGroupAnalysisRun(
  value: unknown, projectId: string, groupId: string,
  expected?: GroupAnalysisRunExpectation,
): GroupAnalysisRun | null {
  if (!isRecord(value)) return null;
  if (typeof value.id !== "string" || !JOB_ID.test(value.id)) return null;
  if (value.project_id !== projectId || value.workflow !== "megabooker.group_additional_analysis") return null;
  if (typeof value.status !== "string" || !JOB_STATUSES.has(value.status)) return null;
  const identity = value.group_analysis;
  const keys = [
    "group_id", "group_version", "snapshot_hash", "request_fingerprint", "source",
  ];
  if (!isRecord(identity) || !hasExactKeys(identity, keys)) return null;
  if (identity.group_id !== groupId || !isInt(identity.group_version, 1, 1_000_000)
    || !isDigest(identity.snapshot_hash) || !isDigest(identity.request_fingerprint)
    || !isSource(identity.source)) return null;
  if (expected) {
    if (expected.groupVersion !== undefined && identity.group_version !== expected.groupVersion) return null;
    if (expected.snapshotHash !== undefined && identity.snapshot_hash !== expected.snapshotHash) return null;
    if (expected.source
      && !sameGroupAnalysisSource(identity.source as OpportunityGroupSource, expected.source)) return null;
  }
  return value as unknown as GroupAnalysisRun;
}

export function parseGroupAnalysisRunPage(
  value: unknown, projectId: string, groupId: string,
  expected?: GroupAnalysisRunExpectation,
): GroupAnalysisRunPage | null {
  if (!isRecord(value) || !hasExactKeys(value, ["items", "next_cursor"])) return null;
  if (!Array.isArray(value.items) || value.items.length > 20) return null;
  const items: GroupAnalysisRun[] = [];
  for (const raw of value.items) {
    const parsed = parseGroupAnalysisRun(raw, projectId, groupId, expected);
    if (!parsed) return null;
    if (items.some((item) => item.id === parsed.id)) return null;
    items.push(parsed);
  }
  if (value.next_cursor !== null && value.next_cursor !== undefined
    && (typeof value.next_cursor !== "string" || !JOB_ID.test(value.next_cursor))) return null;
  return { items, next_cursor: (value.next_cursor as string | null | undefined) ?? null };
}

function parseObservation(value: unknown, pageUrl: string, fetchedAt: string, digest: string): boolean {
  if (!isRecord(value) || !hasExactKeys(value, ["kind", "text", "page_url", "observed_at", "content_hash"])) return false;
  return isText(value.kind, 32) && isText(value.text, 400)
    && value.page_url === pageUrl && value.observed_at === fetchedAt && value.content_hash === digest;
}

function parsePage(value: unknown, root: string): boolean {
  if (!isRecord(value) || !hasExactKeys(value, [
    "url", "fetched_at", "content_hash", "status_code", "title", "description",
    "public_links", "observations",
  ])) return false;
  if (!isUrl(value.url) || !hostInSite(value.url as string, root)
    || !isDateTime(value.fetched_at) || !isDigest(value.content_hash)
    || !isInt(value.status_code, 200, 299)) return false;
  if (value.title !== null && !isText(value.title, 200)) return false;
  if (value.description !== null && !isText(value.description, 500)) return false;
  if (!Array.isArray(value.public_links) || value.public_links.length > 10
    || !value.public_links.every((link) => isUrl(link) && hostInSite(link as string, root))) return false;
  if (!Array.isArray(value.observations) || value.observations.length > 12) return false;
  return value.observations.every((item) => parseObservation(
    item, value.url as string, value.fetched_at as string, value.content_hash as string,
  ));
}

function parseOrganization(value: unknown, expectedId: string): boolean {
  if (!isRecord(value) || !hasExactKeys(value, [
    "organization_id", "organization_name", "existing_evidence", "official_site",
    "comparison", "inference", "unknowns", "conflicts", "semantic_analysis",
    "human_review_status",
  ])) return false;
  if (value.organization_id !== expectedId || !ORG_ID.test(expectedId)) return false;
  if (value.organization_name !== null && !isText(value.organization_name, 200)) return false;
  if (value.semantic_analysis !== "not_evaluated" || value.human_review_status !== "pending") return false;
  const existing = value.existing_evidence;
  if (!isRecord(existing) || !hasExactKeys(existing, [
    "state", "supported_facts", "missing_fields", "uncited_fields", "contradictions", "sources",
  ]) || existing.state !== "available") return false;
  if (!Array.isArray(existing.supported_facts) || existing.supported_facts.length > 24) return false;
  for (const fact of existing.supported_facts) {
    if (!isRecord(fact) || !hasExactKeys(fact, ["field", "value", "source_ids"])
      || !isText(fact.field, 64) || !isText(fact.value, 512)
      || !isStringArray(fact.source_ids, 4, 64)) return false;
  }
  if (!isStringArray(existing.missing_fields, 8, 64) || !isStringArray(existing.uncited_fields, 8, 64)) return false;
  if (!Array.isArray(existing.contradictions) || existing.contradictions.length > 8) return false;
  for (const contradiction of existing.contradictions) {
    if (!isRecord(contradiction) || !hasExactKeys(contradiction, ["field", "values"])
      || !isText(contradiction.field, 64)
      || !isStringArray(contradiction.values, 4, 512)) return false;
  }
  if (!Array.isArray(existing.sources) || existing.sources.length > 6) return false;
  for (const source of existing.sources) {
    if (!isRecord(source) || !hasExactKeys(source, ["source_id", "url", "observed_at"])
      || !isText(source.source_id, 64) || !isUrl(source.url) || !isDate(source.observed_at)) return false;
  }
  const sourceIds = new Set(
    (existing.sources as UnknownRecord[]).map((source) => source.source_id as string),
  );
  for (const fact of existing.supported_facts) {
    if (!(fact.source_ids as string[]).every((id) => sourceIds.has(id))) return false;
  }
  const site = value.official_site;
  if (!isRecord(site) || !hasExactKeys(site, ["state", "host", "pages", "limitations"])) return false;
  if (!["observed", "partial", "unavailable", "not_attempted"].includes(site.state as string)) return false;
  if (site.host !== null && (typeof site.host !== "string" || !HOST.test(site.host))) return false;
  const root = site.host === null ? null : siteRoot(site.host as string);
  if (!Array.isArray(site.pages) || site.pages.length > 5) return false;
  if (root === null && site.pages.length !== 0) return false;
  const pageUrls = new Set<string>();
  for (const page of site.pages) {
    if (!parsePage(page, root as string)) return false;
    pageUrls.add((page as UnknownRecord).url as string);
  }
  if (root !== null) {
    const websiteFact = (existing.supported_facts as UnknownRecord[]).find(
      (fact) => fact.field === "website",
    );
    if (!websiteFact || urlHost(websiteFact.value as string) !== site.host) return false;
  }
  if (!isStringArray(site.limitations, 16, 64)) return false;
  if (new Set(site.limitations as string[]).size !== site.limitations.length) return false;
  if (site.pages.length > 0) {
    if (site.state !== (site.limitations.length ? "partial" : "observed")) return false;
  } else if (site.state !== (site.limitations.length ? "unavailable" : "not_attempted")) {
    return false;
  }
  const comparison = value.comparison;
  if (!isRecord(comparison) || !hasExactKeys(comparison, ["state", "items"])) return false;
  if (!["supported", "differs", "new_observation", "not_comparable", "unavailable"].includes(comparison.state as string)) return false;
  if (!Array.isArray(comparison.items) || comparison.items.length > 8) return false;
  if (pageUrls.size === 0 && comparison.items.length !== 0) return false;
  for (const item of comparison.items) {
    if (!isRecord(item) || !hasExactKeys(item, [
      "field", "prior_value", "observed_value", "relation", "page_url",
    ]) || !isText(item.field, 64) || !isText(item.prior_value, 512)
      || !isText(item.observed_value, item.field === "website" ? MAX_URL : 512)
      || !["agrees", "differs"].includes(item.relation as string)
      || !pageUrls.has(item.page_url as string)) return false;
  }
  const comparisonItems = comparison.items as UnknownRecord[];
  const differsItems = comparisonItems
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.relation === "differs");
  if (comparisonItems.length === 0) {
    const state = comparison.state as string;
    if (pageUrls.size === 0) {
      if (state !== "unavailable") return false;
    } else if (state !== "not_comparable" && state !== "new_observation") {
      return false;
    }
  } else if (differsItems.length > 0) {
    if (comparison.state !== "differs") return false;
  } else if (comparison.state !== "supported") {
    return false;
  }
  if (!Array.isArray(value.inference) || value.inference.length > 3) return false;
  for (const item of value.inference) {
    if (!isRecord(item) || !hasExactKeys(item, ["kind", "text", "basis", "page_url"])
      || item.kind !== "deterministic_inference" || !isText(item.text, 400)
      || !isText(item.basis, 300) || !pageUrls.has(item.page_url as string)) return false;
  }
  if (!Array.isArray(value.unknowns) || value.unknowns.length > 16) return false;
  const seenUnknowns = new Set<string>();
  for (const item of value.unknowns) {
    if (!isRecord(item) || !hasExactKeys(item, ["kind", "reason"])
      || !["official_host", "official_site"].includes(item.kind as string)
      || !isText(item.reason, 64)) return false;
    const identity = `${item.kind as string}:${item.reason as string}`;
    if (seenUnknowns.has(identity)) return false;
    seenUnknowns.add(identity);
    if (item.kind === "official_host" && root !== null) return false;
    if (item.kind === "official_site" && !(site.limitations as string[]).includes(item.reason as string)) return false;
  }
  if (root === null && ![...seenUnknowns].some((key) => key.startsWith("official_host:"))) return false;
  if (!Array.isArray(value.conflicts) || value.conflicts.length > 8) return false;
  if (value.conflicts.length !== differsItems.length) return false;
  const usedDiffers = new Set<number>();
  for (const item of value.conflicts) {
    if (!isRecord(item) || !hasExactKeys(item, ["field", "values", "basis"])
      || !isText(item.field, 64) || !Array.isArray(item.values)
      || item.values.length !== 2
      || !isText(item.values[0], 512)
      || !isText(item.values[1], item.field === "website" ? MAX_URL : 512)
      || !isText(item.basis, 300)) return false;
    const values = item.values as string[];
    const match = differsItems.find(({ item: candidate, index }) => (
      !usedDiffers.has(index)
      && candidate.field === item.field
      && candidate.prior_value === values[0]
      && candidate.observed_value === values[1]
    ));
    if (!match) return false;
    usedDiffers.add(match.index);
  }
  if (usedDiffers.size !== differsItems.length) return false;
  return true;
}

export function parseGroupAnalysisResult(
  value: unknown, projectId: string, groupId: string,
  expected?: GroupAnalysisResultExpectation,
): GroupAnalysisResult | null {
  if (!isRecord(value) || value.schema_version !== "group_analysis.result.v1") return null;
  if (value.state === "unavailable") {
    if (!["schema_version", "state", "reason", "job_status"].every((key) => key in value)) return null;
    if (Object.keys(value).some((key) => !["schema_version", "state", "reason", "job_status", "progress"].includes(key))) return null;
    if (typeof value.reason !== "string" || !isText(value.reason, 64)
      || typeof value.job_status !== "string" || !JOB_STATUSES.has(value.job_status)) return null;
    if (value.progress !== undefined) {
      if (!isRecord(value.progress) || !hasExactKeys(value.progress, ["completed", "unknown", "failed", "intent"])
        || !isInt(value.progress.completed, 0, 200) || !isInt(value.progress.unknown, 0, 200)
        || !isInt(value.progress.failed, 0, 200) || !isInt(value.progress.intent, 0, 200)) return null;
    }
    return value as unknown as GroupAnalysisResult;
  }
  if (value.state !== "available") return null;
  const keys = ["schema_version", "state", "reason", "workflow", "project_id", "group",
    "source", "question", "snapshot_hash", "job_status", "capabilities", "limits",
    "summary", "organizations"];
  const allowed = new Set([...keys, "cost"]);
  if (Object.keys(value).some((key) => !allowed.has(key)) || !keys.every((key) => key in value)) {
    return null;
  }
  if (value.reason !== null || value.project_id !== projectId || !isDigest(value.snapshot_hash)
    || value.job_status !== "succeeded" || !isSource(value.source)) return null;
  const workflow = value.workflow;
  if (!isRecord(workflow) || !hasExactKeys(workflow, ["key", "version", "content_hash"])
    || workflow.key !== "megabooker.group_additional_analysis" || workflow.version !== 1
    || !isDigest(workflow.content_hash)) return null;
  const group = value.group;
  if (!isRecord(group) || !hasExactKeys(group, ["id", "version", "name", "organization_count"])
    || group.id !== groupId || !isInt(group.version, 1, 1_000_000)
    || !isText(group.name, 80) || !group.name.trim()
    || !isInt(group.organization_count, 1, MAX_ORGANIZATIONS)) return null;
  if (expected) {
    if (group.version !== expected.groupVersion
      || (expected.snapshotHash !== undefined && value.snapshot_hash !== expected.snapshotHash)
      || !sameGroupAnalysisSource(value.source as OpportunityGroupSource, expected.source)) {
      return null;
    }
  }
  const question = value.question;
  if (!isRecord(question) || !hasExactKeys(question, ["text", "hash"])
    || typeof question.text !== "string" || question.text.length > 2000
    || !isDigest(question.hash)) return null;
  const caps = parseCaps(value.capabilities, "completed");
  if (!caps || !parseLimits(value.limits)) return null;
  if (value.cost !== undefined) {
    const cost = value.cost;
    if (!isRecord(cost) || !hasExactKeys(cost, ["known", "actual_micros", "estimated_micros",
      "unavailable_count", "currency", "basis", "semantic_cost"])) return null;
    if (cost.known !== true || cost.actual_micros !== 0 || cost.estimated_micros !== 0
      || cost.unavailable_count !== 0 || cost.currency !== "USD"
      || cost.basis !== "measured_zero_no_paid_service") return null;
    const semanticCost = cost.semantic_cost;
    if (!isRecord(semanticCost) || !hasExactKeys(semanticCost, ["status", "reason"])
      || semanticCost.status !== "unknown" || semanticCost.reason !== "provider_not_configured") return null;
  }
  const summary = value.summary;
  if (!isRecord(summary) || !hasExactKeys(summary, ["organizations", "eligible_hosts",
    "pages_fetched", "pages_unavailable", "new_observations", "conflicts", "unknowns",
    "semantic_not_evaluated"]) || !isInt(summary.organizations, 1, MAX_ORGANIZATIONS)) return null;
  for (const key of ["eligible_hosts", "pages_fetched", "pages_unavailable", "new_observations",
    "conflicts", "unknowns", "semantic_not_evaluated"] as const) {
    if (!isInt(summary[key], 0, 100)) return null;
  }
  const organizations = value.organizations;
  if (!Array.isArray(organizations) || organizations.length !== summary.organizations
    || organizations.length !== group.organization_count) return null;
  const seen = new Set<string>();
  let pagesFetched = 0;
  let pagesUnavailable = 0;
  let newObservations = 0;
  let conflicts = 0;
  let eligibleHosts = 0;
  for (const organization of organizations) {
    if (!isRecord(organization) || typeof organization.organization_id !== "string"
      || !parseOrganization(organization, organization.organization_id)
      || seen.has(organization.organization_id)) return null;
    seen.add(organization.organization_id);
    const site = organization.official_site as UnknownRecord;
    const pages = site.pages as UnknownRecord[];
    pagesFetched += pages.length;
    if (pages.length === 0) pagesUnavailable += 1;
    newObservations += pages.reduce(
      (count, page) => count + (page.observations as unknown[]).length, 0,
    );
    conflicts += (organization.conflicts as unknown[]).length;
    if (site.host !== null) eligibleHosts += 1;
  }
  if (summary.semantic_not_evaluated !== organizations.length
    || summary.eligible_hosts !== eligibleHosts
    || summary.pages_fetched !== pagesFetched
    || summary.pages_unavailable !== pagesUnavailable
    || summary.new_observations !== newObservations
    || summary.conflicts !== conflicts
    || summary.unknowns !== pagesUnavailable) return null;
  if (caps.official_site_research !== (pagesFetched > 0 ? "completed" : "unavailable")) {
    return null;
  }
  return value as unknown as GroupAnalysisResult;
}
